/* ============================================================
   SMILE BOXX — Split Hero Journey
   Architecture: mirrors smileboxx-product-journey.js exactly.
   runway spacer + position:fixed proxies + endTrigger: carousel
   ============================================================ */
(function () {
  'use strict';

  var COLORS = ['pink', 'green', 'blue', 'purple', 'orange'];
  var SEL = {
    hero: '[data-sbsplit-hero]',
    runway: '[data-sbsplit-runway]',
    source: '[data-sbsplit-source]',
    visual: '[data-sbsplit-visual]',
    seed: '[data-sbsplit-seed]',
    bank: '[data-sbsplit-bank]',
    carousel: '[data-journey-carousel]',
    track: '.sb-carousel__track',
  };

  /* Thresholds — identical feel to original */
  var S = {
    swapAt: 0.015,
    handoffAt: 0.04,
    midpoint: 0.38 /* single proxy reaches midpoint & starts splitting */,
    splitEnd: 0.56 /* 5 proxies fully emerged */,
    landAt: 0.992,
    scaleShift: 0.82,
    destVpY: 0.58,
  };

  function clamp(v, lo, hi) {
    return Math.min(hi, Math.max(lo, v));
  }
  function lerp(a, b, t) {
    return a + (b - a) * t;
  }
  function map(v, lo, hi) {
    if (hi <= lo) return v >= hi ? 1 : 0;
    return clamp((v - lo) / (hi - lo), 0, 1);
  }
  function ease(t) {
    var c = clamp(t, 0, 1);
    return c * c * (3 - 2 * c);
  }
  function debounce(fn, ms) {
    var t;
    return function () {
      var c = this,
        a = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(c, a);
      }, ms);
    };
  }

  /* ============================================================ */

  function SplitJourney(hero, carousel) {
    this.hero = hero;
    this.carousel = carousel;
    this.runway = hero.querySelector(SEL.runway);
    this.srcShell = hero.querySelector(SEL.source);
    this.srcVisual = hero.querySelector(SEL.visual);
    this.seedImg = hero.querySelector(SEL.seed);
    this.bank = hero.querySelector(SEL.bank);
    this.track = carousel.querySelector(SEL.track);

    this.trigger = null;
    this.mmatch = null;
    this.active = false;
    this.metrics = null;
    this.topOff = 0;
    this.ro = null;
    this.cleanups = [];

    this.singleProxy = null; /* main hero ampoule proxy */
    this.colorProxies = []; /* [{el,color}] — 5 color proxies */

    /* If page is loaded/reloaded already scrolled down, abort animation to prevent glitches */
    this.aborted = (window.scrollY || window.pageYOffset || 0) > 50;

    this.requestRefresh = debounce(this._refresh.bind(this), 120);
  }

  SplitJourney.prototype._valid = function () {
    return !!(this.runway && this.srcShell && this.srcVisual);
  };

  SplitJourney.prototype.mount = function () {
    if (!this._valid()) return;
    this.mmatch = gsap.matchMedia();
    this.mmatch.add('(prefers-reduced-motion: no-preference)', this._enable.bind(this));
  };

  SplitJourney.prototype._enable = function () {
    if (this.active) return undefined;
    this.active = true;
    this.topOff = 0;

    if (window.innerWidth < 990 || this.aborted) {
      this.hero.classList.add('sbhs--fallback');
      return this._disable.bind(this);
    }

    this.hero.classList.add('sbhs--journey-ready');
    this.carousel.classList.add('sbhs-carousel--journey-ready');
    this._buildProxies();
    this._bind();

    var self = this;
    this.trigger = ScrollTrigger.create({
      trigger: this.runway,
      start: 'top bottom',
      endTrigger: this.track || this.carousel,
      end: 'center ' + S.destVpY * 100 + '%',
      scrub: true,
      invalidateOnRefresh: true,
      onRefresh: function (st) {
        self._onRefresh(st);
      },
      onUpdate: function (st) {
        self._onUpdate(st);
      },
    });

    ScrollTrigger.refresh();
    return this._disable.bind(this);
  };

  SplitJourney.prototype._disable = function () {
    this.active = false;
    this.hero.classList.remove('sbhs--journey-ready', 'sbhs--fallback');
    this.carousel.classList.remove('sbhs-carousel--journey-ready', 'sbhs--carousel-landed');

    if (this.trigger) {
      this.trigger.kill();
      this.trigger = null;
    }
    if (this.ro) {
      this.ro.disconnect();
      this.ro = null;
    }
    var cb;
    while ((cb = this.cleanups.pop())) cb();

    if (this.singleProxy) {
      this.singleProxy.remove();
      this.singleProxy = null;
    }
    this.colorProxies.forEach(function (p) {
      p.el.remove();
    });
    this.colorProxies = [];

    if (this.srcVisual) gsap.set(this.srcVisual, { clearProps: 'opacity,pointerEvents' });
    this.metrics = null;
  };

  SplitJourney.prototype.destroy = function () {
    if (this.mmatch) {
      this.mmatch.revert();
      this.mmatch = null;
    }
    this._disable();
  };

  /* ── Proxy construction ── */

  SplitJourney.prototype._buildProxies = function () {
    this.singleProxy = this._mkProxy('sbhs-proxy sbhs-proxy--single', this.seedImg);
    document.body.appendChild(this.singleProxy);

    var self = this;
    this.colorProxies = COLORS.map(function (color) {
      var bankImg = self.bank ? self.bank.querySelector('[data-sbsplit-ampoule="' + color + '"]') : null;
      var el = self._mkProxy('sbhs-proxy sbhs-proxy--' + color, bankImg);
      document.body.appendChild(el);
      return { el: el, color: color };
    });
  };

  SplitJourney.prototype._mkProxy = function (cls, img) {
    var el = document.createElement('div');
    el.className = cls;
    el.setAttribute('aria-hidden', 'true');
    if (img) {
      var c = img.cloneNode(false);
      c.removeAttribute('data-sbsplit-seed');
      c.removeAttribute('data-sbsplit-ampoule');
      c.removeAttribute('loading');
      c.removeAttribute('fetchpriority');
      c.setAttribute('decoding', 'async');
      c.className = 'sbhs-proxy__img';
      el.appendChild(c);
    } else {
      var s = document.createElement('div');
      s.className = 'sbhs-proxy__shape sbhs-proxy__shape--pink';
      el.appendChild(s);
    }
    return el;
  };

  /* ── Observers ── */

  SplitJourney.prototype._bind = function () {
    var r = this.requestRefresh;
    window.addEventListener('resize', r, { passive: true });
    this.cleanups.push(function () {
      window.removeEventListener('resize', r);
    });

    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(r);
      [this.srcShell, this.track].filter(Boolean).forEach(
        function (n) {
          this.ro.observe(n);
        }.bind(this),
      );
    }

    if (this.seedImg && !this.seedImg.complete) {
      this.seedImg.addEventListener('load', r);
      this.seedImg.addEventListener('error', r);
      this.cleanups.push(
        function () {
          this.seedImg.removeEventListener('load', r);
          this.seedImg.removeEventListener('error', r);
        }.bind(this),
      );
    }
  };

  SplitJourney.prototype._refresh = function () {
    if (this.active && this.trigger && ScrollTrigger) ScrollTrigger.refresh();
  };

  /* ── Measurement — mirrors original measure() ── */

  SplitJourney.prototype._measure = function () {
    var vw = window.innerWidth,
      vh = window.innerHeight;
    var scrollY = window.scrollY || window.pageYOffset || 0;
    var srcR = this.srcShell.getBoundingClientRect();
    var srcW = this.srcVisual.offsetWidth || this.seedImg.offsetWidth || this.srcShell.offsetWidth || srcR.width || 200;
    var srcH = this.srcVisual.offsetHeight || this.seedImg.offsetHeight || srcW * ((this.seedImg.naturalHeight || 1) / (this.seedImg.naturalWidth || 1));
    var srcCX = srcR.left + srcR.width / 2;
    var srcCY = srcR.top + srcR.height / 2;
    var startRot = 54;

    /* Midpoint — viewport center area, like original */
    var midCX = lerp(srcCX, vw * 0.5, 0.54);
    var midCY = clamp(vh * 0.42, vh * 0.38, vh * 0.46);

    /* Color proxies use the exact same dimensions as the source ampoule.
       This makes the split feel like one object becoming five, not
       five small copies suddenly appearing. Scale down only at landing. */
    var cW = srcW;
    var cH = srcH;

    /* Carousel card destinations */
    var cards = this._measureCards(cW, cH, scrollY, vw, vh);

    return {
      vw: vw,
      vh: vh,
      srcW: srcW,
      srcH: srcH,
      srcX: srcCX - srcW / 2,
      srcY: srcCY - srcH / 2,
      midX: midCX - srcW / 2,
      midY: midCY - srcH / 2,
      startRot: startRot,
      cW: cW,
      cH: cH,
      cards: cards,
    };
  };

  SplitJourney.prototype._measureCards = function (cW, cH, scrollY, vw, vh) {
    var self = this;
    return COLORS.map(function (color, i) {
      var card = self.carousel.querySelector('.sb-carousel__card--' + color);
      if (!card) {
        var all = self.carousel.querySelectorAll('.sb-carousel__card');
        card = all[i] || null;
      }
      if (card) {
        var vis = card.querySelector('.sb-carousel__card-visual') || card;
        var r = vis.getBoundingClientRect();
        
        var natW = self.seedImg ? (self.seedImg.naturalWidth || 1) : 1;
        var natH = self.seedImg ? (self.seedImg.naturalHeight || 1) : 1;
        var A = natW / natH;
        
        var destW = (r.width / r.height > A) ? (r.height * A) : r.width;
        var proxW = (cW / cH > A) ? (cH * A) : cW;
        var exactScale = destW / proxW;

        return {
          cx: r.left + r.width / 2,
          cy: vh * S.destVpY,
          absY: scrollY + r.top + r.height * 0.35,
          scale: clamp(exactScale, 0.3, 2.0),
        };
      }
      var step = (vw * 0.7) / Math.max(COLORS.length - 1, 1);
      return { cx: vw * 0.15 + i * step, cy: vh * 0.58, absY: scrollY + vh * 0.58, scale: 0.8 };
    });
  };

  /* ── Render — mirrors original's source→mid→dest pattern ── */

  SplitJourney.prototype._render = function (progress) {
    if (!this.metrics) return;
    var m = this.metrics,
      G = gsap;
    var p = clamp(map(progress, this.topOff, 1), 0, 1);
    var scrollY = window.scrollY || window.pageYOffset || 0;
    var landed = p >= S.landAt;
    var handoffP = map(p, 0, S.handoffAt);
    var splitP = ease(map(p, S.midpoint, S.splitEnd));

    /* ── Source image ── */
    G.set(this.srcVisual, {
      opacity: landed ? 0 : p < S.swapAt ? 1 : Math.max(0, 1 - handoffP),
      pointerEvents: p > S.handoffAt ? 'none' : '',
    });

    /* ── Single proxy — source→midpoint, original two-phase path ── */
    var singleX, singleY;
    if (p <= S.midpoint) {
      var lp = map(p, 0, S.midpoint);
      singleX = lerp(m.srcX, m.midX, lp);
      singleY = lerp(m.srcY, m.midY, lp);
    } else {
      singleX = m.midX;
      singleY = m.midY;
    }
    var sRot = lerp(m.startRot, 0, p);
    var sOp;
    if (p < S.swapAt) sOp = 0;
    else if (p < S.handoffAt) sOp = handoffP;
    else if (p < S.splitEnd) sOp = Math.max(0, 1 - splitP * 2);
    else sOp = 0;

    G.set(this.singleProxy, {
      width: m.srcW,
      height: m.srcH,
      x: singleX,
      y: singleY,
      rotation: sRot,
      scale: 1,
      opacity: Math.max(0, sOp),
    });

    /* ── 5 color proxies — emerge at midpoint, travel to carousel ── */
    /* Spread gap: space full-size proxies evenly across ~80% of viewport width */
    var spreadGap = Math.min(m.cW * 1.1, m.vw * 0.18);

    for (var i = 0; i < this.colorProxies.length; i++) {
      var proxy = this.colorProxies[i];
      var dest = m.cards[i];
      if (!dest) continue;
      var stagger = i * 0.02;

      /* Split emergence: emerge from single proxy position */
      var mySplitP = ease(map(p, S.midpoint + stagger, S.splitEnd + stagger * 0.4));

      /* Origin = single proxy's current center */
      var originCX = singleX + m.srcW / 2;
      var originCY = singleY + m.srcH / 2;

      /* Spread = fan out horizontally from center */
      var spreadCX = m.vw / 2 + (i - 2) * spreadGap;
      var spreadCY = m.midY + m.srcH / 2 + m.vh * 0.05;

      /* Destination = fixed viewport coordinate, matching ScrollTrigger end */
      var destCX = dest.cx;
      var destCY = dest.cy;

      /* Two-phase path: origin→spread during split, then spread→dest during travel */
      var cx, cy;
      if (p < S.splitEnd) {
        cx = lerp(originCX, spreadCX, mySplitP);
        cy = lerp(originCY, spreadCY, mySplitP);
      } else {
        var travelP = map(p, S.splitEnd, 1);
        cx = lerp(spreadCX, destCX, travelP);
        cy = lerp(spreadCY, destCY, travelP);
      }

      /* Opacity */
      var cOp;
      if (p < S.midpoint) cOp = 0;
      else if (p < S.splitEnd) cOp = mySplitP;
      else if (!landed) cOp = 1;
      else cOp = 0;

      /* Rotation: slight tilt during travel, straighten for landing */
      var tilts = [-7, -3.5, 0, 3.5, 7];
      var cRot;
      if (p < S.splitEnd) cRot = lerp(0, tilts[i], mySplitP);
      else cRot = lerp(tilts[i], 0, map(p, S.splitEnd, S.landAt));

      /* Scale: grow from small during split, shrink into card at end */
      var cScale;
      /* Scale: start at 1. Shrink perfectly into the carousel card slot at landing. */
      if (p > S.scaleShift) cScale = lerp(1, dest.scale, map(p, S.scaleShift, 1));
      else cScale = 1;

      G.set(proxy.el, {
        width: m.cW,
        height: m.cH,
        x: cx - m.cW / 2,
        y: cy - m.cH / 2,
        rotation: cRot,
        scale: cScale,
        opacity: Math.max(0, cOp),
      });
    }

    this.carousel.classList.toggle('sbhs--carousel-landed', landed);
  };

  /* ── ScrollTrigger callbacks ── */

  SplitJourney.prototype._onRefresh = function (st) {
    var scrollY = window.scrollY || window.pageYOffset || 0;
    if (scrollY <= 6 && st) this.topOff = clamp(st.progress || 0, 0, 0.18);
    this.metrics = this._measure();
    this._render(st.progress);
  };

  SplitJourney.prototype._onUpdate = function (st) {
    if (!this.metrics) this.metrics = this._measure();
    this._render(st.progress);
  };

  /* ── Manager ── */

  var Mgr = {
    instances: [],
    bound: false,
    init: function () {
      if (!window.gsap || !window.ScrollTrigger) {
        setTimeout(function () {
          Mgr.init();
        }, 300);
        return;
      }
      gsap.registerPlugin(ScrollTrigger);
      this.destroy();
      var heroes = document.querySelectorAll(SEL.hero);
      var carousels = document.querySelectorAll(SEL.carousel);
      if (!carousels.length) carousels = document.querySelectorAll('.section--sb-carousel');
      for (var h = 0; h < heroes.length; h++) {
        var hero = heroes[h],
          carousel = null;
        for (var c = 0; c < carousels.length; c++) {
          if (hero.compareDocumentPosition(carousels[c]) & Node.DOCUMENT_POSITION_FOLLOWING) {
            carousel = carousels[c];
            break;
          }
        }
        if (!carousel) continue;
        var inst = new SplitJourney(hero, carousel);
        inst.mount();
        this.instances.push(inst);
      }
      requestAnimationFrame(function () {
        ScrollTrigger.refresh();
      });
    },
    destroy: function () {
      this.instances.forEach(function (i) {
        i.destroy();
      });
      this.instances = [];
    },
    bind: function () {
      if (this.bound) return;
      this.bound = true;
      var ri = function () {
        requestAnimationFrame(function () {
          Mgr.init();
        });
      };
      document.addEventListener('shopify:section:load', ri);
      document.addEventListener('shopify:section:unload', ri);
      document.addEventListener('shopify:section:reorder', ri);
      window.addEventListener('load', function () {
        if (ScrollTrigger) ScrollTrigger.refresh();
      });
    },
  };

  function boot() {
    if (window.SmileBoxxHeroSplit && typeof window.SmileBoxxHeroSplit.destroy === 'function')
      window.SmileBoxxHeroSplit.destroy();
    window.SmileBoxxHeroSplit = Mgr;
    Mgr.bind();
    Mgr.init();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
